import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Props {
  clientId: string;
  initialName?: string | null;
  initialAddress?: string | null;
  initialPhone?: string | null;
  initialLiquidationDate?: string | null;
  initialProtestFileDate?: string | null;
  initialClientNotes?: string | null;
  onCompleted: (data: { name: string; address: string; phone: string }) => void;
}

export default function ProfileCompletion({
  clientId,
  initialName,
  initialAddress,
  initialPhone,
  initialLiquidationDate,
  initialProtestFileDate,
  initialClientNotes,
  onCompleted,
}: Props) {
  const [name, setName] = useState(initialName || '');
  const [address, setAddress] = useState(initialAddress || '');
  const [phone, setPhone] = useState(initialPhone || '');
  const [liquidationDate, setLiquidationDate] = useState(initialLiquidationDate || '');
  const [protestFileDate, setProtestFileDate] = useState(initialProtestFileDate || '');
  const [clientNotes, setClientNotes] = useState(initialClientNotes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isComplete = name.trim().length >= 2 && address.trim().length >= 5 && phone.trim().length >= 7;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete || saving) return;
    setSaving(true);
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          importer_of_record_name: name.trim(),
          importer_of_record_address: address.trim(),
          importer_of_record_phone: phone.trim(),
          liquidation_date: liquidationDate || null,
          protest_file_date: protestFileDate || null,
          client_notes: clientNotes.trim() || null,
        })
        .eq('id', clientId);
      if (updateError) throw updateError;
      onCompleted({ name: name.trim(), address: address.trim(), phone: phone.trim() });
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-[#FDB913]/15 to-[#FDB913]/5 border-2 border-[#FDB913]/60 rounded-2xl p-6 mb-6">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/20 rounded-xl border border-[#FDB913]/40 flex-shrink-0">
          <i className="ri-user-settings-fill text-[#FDB913] text-2xl"></i>
        </div>
        <div>
          <p className="text-white font-black text-base">Complete Your Profile</p>
          <p className="text-white/60 text-xs mt-0.5">
            Required for CBP filing — these details identify the Importer of Record on all CBP submissions.
          </p>
        </div>
        <span className="ml-auto flex-shrink-0 inline-flex items-center gap-1 bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap">
          <i className="ri-error-warning-fill text-xs"></i>Required
        </span>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        {/* Importer of Record Name */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Importer of Record Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            maxLength={200}
            placeholder="Legal entity name as it appears on CBP forms"
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#FDB913]/70 transition-colors"
          />
          <p className="text-white/30 text-xs mt-1">
            The legal name of your business or individual as the Importer of Record on file with CBP
          </p>
        </div>

        {/* Address of Importer of Record */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Address of Importer of Record <span className="text-red-400">*</span>
          </label>
          <textarea
            value={address}
            onChange={e => setAddress(e.target.value)}
            required
            maxLength={500}
            rows={2}
            placeholder="Street address, city, state, ZIP / country — full mailing address"
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#FDB913]/70 transition-colors resize-none"
          />
          <p className="text-white/30 text-xs mt-1">
            Official business address registered with CBP for this import account
          </p>
        </div>

        {/* Telephone No. */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Telephone No. of Importer of Record <span className="text-red-400">*</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            required
            maxLength={30}
            placeholder="+1 (XXX) XXX-XXXX"
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#FDB913]/70 transition-colors"
          />
          <p className="text-white/30 text-xs mt-1">
            Direct phone number for the Importer of Record — used on CBP Form 4811 and correspondence
          </p>
        </div>

        {/* Liquidation Date (optional) */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Liquidation Date <span className="text-white/30 font-normal normal-case tracking-normal">(if applicable)</span>
          </label>
          <input
            type="date"
            value={liquidationDate}
            onChange={e => setLiquidationDate(e.target.value)}
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm focus:outline-none focus:border-[#FDB913]/70 transition-colors"
          />
          <p className="text-white/30 text-xs mt-1">
            The date CBP liquidated your entry, if known. Found on your CBP Form 4333-A (Courtesy Notice of Liquidation).
          </p>
        </div>

        {/* Protest File Date (optional) */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Protest File Date <span className="text-white/30 font-normal normal-case tracking-normal">(if applicable)</span>
          </label>
          <input
            type="date"
            value={protestFileDate}
            onChange={e => setProtestFileDate(e.target.value)}
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm focus:outline-none focus:border-[#FDB913]/70 transition-colors"
          />
          <p className="text-white/30 text-xs mt-1">
            The date a protest was filed with CBP, if one has already been submitted. Protests must be filed within 180 days of liquidation.
          </p>
        </div>

        {/* Notes / Additional Information */}
        <div>
          <label className="block text-xs font-black text-[#FDB913]/80 uppercase tracking-wider mb-1.5">
            Notes / Additional Information <span className="text-white/30 font-normal normal-case tracking-normal">(optional)</span>
          </label>
          <textarea
            value={clientNotes}
            onChange={e => setClientNotes(e.target.value.slice(0, 500))}
            rows={4}
            maxLength={500}
            placeholder="Any additional context, special circumstances, or notes you'd like to share with your attorney — e.g. entry numbers, port of entry, shipment details, prior correspondence..."
            className="w-full px-4 py-3 bg-black/40 border border-[#FDB913]/30 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#FDB913]/70 transition-colors resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-white/30 text-xs">
              Visible only to your attorney. Not shared with CBP.
            </p>
            <p className={`text-xs ${clientNotes.length >= 450 ? 'text-amber-400' : 'text-white/25'}`}>
              {clientNotes.length}/500
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
            <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!isComplete || saving}
          className="w-full bg-[#FDB913] hover:bg-[#e6a800] disabled:opacity-40 text-black font-black text-sm py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
          ) : (
            <><i className="ri-save-line"></i>Save Profile Info</>
          )}
        </button>
      </form>
    </div>
  );
}
